import React, { useState, useCallback } from "react";
import { Typography, Tabs, Input, Select, Tag, Divider, Result } from "antd";
import {
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import debounce from "lodash.debounce";
import moment from "moment";
import { ModalItem, ButtonItem } from "../../../../Components/Items";
import SelectorPlantillas from "./Selectores/SelectorPlantillas";
import { WA } from "../theme";

const { Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

const ModalMensajeMasivo = ({
  visible,
  headersToken,
  empresaId,
  onEnviarMasivo,
  onCancel,
}) => {
  const [fase, setFase] = useState("destinatarios");
  const [telefonosManual, setTelefonosManual] = useState("");
  const [contactosSeleccionados, setContactosSeleccionados] = useState([]);
  const [contactoOpciones, setContactoOpciones] = useState([]);
  const [buscandoContactos, setBuscandoContactos] = useState(false);
  const [errorDestinatarios, setErrorDestinatarios] = useState("");
  const [resultado, setResultado] = useState(null);

  const headers = { headers: headersToken };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const buscarContactos = useCallback(
    debounce((texto) => {
      setBuscandoContactos(true);
      axios
        .post("contactos/selectContactosItem", { query: texto || "", empresaId }, headers)
        .then((r) => {
          if (r.data.success === 2) setContactoOpciones(r.data.contactos || []);
        })
        .catch(() => {})
        .finally(() => setBuscandoContactos(false));
    }, 300),
    [empresaId, headersToken],
  );

  const telefonosDeContactos = contactosSeleccionados.map((c) => c.numero).filter(Boolean);

  const obtenerTodosLosTelefonos = () => {
    const manuales = telefonosManual
      .split(/[\n,;]/)
      .map((t) => t.trim())
      .filter((t) => t.replace(/\D/g, "").length >= 8);
    return [...new Set([...manuales, ...telefonosDeContactos])];
  };

  const handleSiguiente = () => {
    const todos = obtenerTodosLosTelefonos();
    if (todos.length === 0) {
      setErrorDestinatarios("Agrega al menos un número válido (manual o desde Contactos).");
      return;
    }
    if (todos.length > 500) {
      setErrorDestinatarios("Máximo 500 destinatarios por envío.");
      return;
    }
    setErrorDestinatarios("");
    setFase("plantilla");
  };

  const handleEnviar = (fd) => {
    const telefonos = obtenerTodosLosTelefonos();
    telefonos.forEach((t) => fd.append("telefonos", t));
    onEnviarMasivo(fd, (res) => {
      setResultado(res);
      setFase("resultado");
    });
  };

  const handleCerrar = () => {
    setFase("destinatarios");
    setTelefonosManual("");
    setContactosSeleccionados([]);
    setContactoOpciones([]);
    setErrorDestinatarios("");
    setResultado(null);
    onCancel();
  };

  if (!visible) return null;

  const opcionesVisibles = [
    ...contactosSeleccionados,
    ...contactoOpciones.filter((o) => !contactosSeleccionados.find((c) => c._id === o._id)),
  ];

  return (
    <>
      <ModalItem
        title="Mensaje masivo"
        visible={fase === "destinatarios"}
        onCancel={handleCerrar}
        spinning={false}
        footer={null}
        width={520}
      >
        <Text style={{ color: WA.textSecondary, fontSize: 13, display: "block", marginBottom: 16 }}>
          Selecciona los destinatarios. Solo se pueden enviar plantillas aprobadas.
        </Text>

        <Tabs defaultActiveKey="manual">
          <TabPane tab="Manual" key="manual">
            <TextArea
              value={telefonosManual}
              onChange={(e) => {
                setTelefonosManual(e.target.value);
                setErrorDestinatarios("");
              }}
              placeholder={"Un número por línea:\n5512345678\n+525598765432"}
              rows={6}
              style={{ fontFamily: "monospace", fontSize: 13 }}
            />
          </TabPane>
          <TabPane tab="Contactos" key="contactos">
            <Select
              mode="multiple"
              showSearch
              filterOption={false}
              onSearch={buscarContactos}
              onFocus={() => buscarContactos("")}
              loading={buscandoContactos}
              placeholder="Buscar contactos por nombre o teléfono..."
              style={{ width: "100%" }}
              value={contactosSeleccionados.map((c) => c._id)}
              onChange={(ids) => {
                const sel = ids
                  .map(
                    (id) =>
                      contactosSeleccionados.find((c) => c._id === id) ||
                      contactoOpciones.find((c) => c._id === id),
                  )
                  .filter(Boolean);
                setContactosSeleccionados(sel);
                setErrorDestinatarios("");
              }}
              notFoundContent={buscandoContactos ? "Buscando..." : "No se encontraron contactos"}
            >
              {opcionesVisibles.map((contacto) => {
                const tieneVentana =
                  contacto.ventanaAbiertaHasta &&
                  moment(contacto.ventanaAbiertaHasta).isAfter();
                return (
                  <Option key={contacto._id} value={contacto._id}>
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ flex: 1 }}>
                        {contacto.stringBuscar || contacto.nombre || contacto._id}
                      </span>
                      {tieneVentana && (
                        <Tag
                          color="green"
                          style={{ margin: 0, fontSize: 10, lineHeight: "16px" }}
                          icon={<ClockCircleOutlined />}
                        >
                          {moment(contacto.ventanaAbiertaHasta).format("HH:mm")}
                        </Tag>
                      )}
                      {!contacto.numero && (
                        <Tag
                          color="orange"
                          style={{ margin: 0, fontSize: 10, lineHeight: "16px" }}
                        >
                          sin tel.
                        </Tag>
                      )}
                    </span>
                  </Option>
                );
              })}
            </Select>
            {telefonosDeContactos.length > 0 && (
              <Text style={{ fontSize: 12, color: WA.textSecondary, display: "block", marginTop: 6 }}>
                {telefonosDeContactos.length} teléfono(s) de contactos seleccionados
              </Text>
            )}
          </TabPane>
        </Tabs>

        {errorDestinatarios && (
          <Text style={{ color: "#ff4d4f", fontSize: 12, display: "block", marginTop: 8 }}>
            {errorDestinatarios}
          </Text>
        )}

        <Text style={{ fontSize: 12, color: WA.textSecondary, display: "block", marginTop: 8 }}>
          Total: {obtenerTodosLosTelefonos().length} número(s)
        </Text>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, gap: 8 }}>
          <ButtonItem onClick={handleCerrar}>Cancelar</ButtonItem>
          <ButtonItem
            type="primary"
            lima
            onClick={handleSiguiente}
            disabled={obtenerTodosLosTelefonos().length === 0}
          >
            Siguiente → elegir plantilla
          </ButtonItem>
        </div>
      </ModalItem>

      <SelectorPlantillas
        visible={fase === "plantilla"}
        headersToken={headersToken}
        empresaId={empresaId}
        onCancel={() => setFase("destinatarios")}
        onEnviar={handleEnviar}
      />

      <ModalItem
        title="Resultado del envío"
        visible={fase === "resultado"}
        onCancel={handleCerrar}
        spinning={false}
        footer={null}
        width={440}
      >
        {resultado && (
          <>
            <Result
              icon={
                resultado.fallidos.length === 0 ? (
                  <CheckCircleOutlined style={{ color: WA.lima }} />
                ) : (
                  <SendOutlined style={{ color: WA.lima }} />
                )
              }
              title={`${resultado.enviados} de ${resultado.total} enviados`}
              subTitle={
                resultado.fallidos.length > 0
                  ? `${resultado.fallidos.length} envío(s) fallaron`
                  : "Todos los mensajes se enviaron correctamente."
              }
            />
            {resultado.fallidos.length > 0 && (
              <>
                <Divider>
                  <Text style={{ fontSize: 12, color: WA.textSecondary }}>Números fallidos</Text>
                </Divider>
                <div
                  style={{
                    maxHeight: 180,
                    overflowY: "auto",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    padding: "4px 0",
                  }}
                >
                  {resultado.fallidos.map((f) => (
                    <Tag
                      key={f.telefono}
                      color="orange"
                      icon={<CloseCircleOutlined />}
                      title={f.error}
                    >
                      {f.telefono}
                    </Tag>
                  ))}
                </div>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <ButtonItem type="primary" lima onClick={handleCerrar}>
                Cerrar
              </ButtonItem>
            </div>
          </>
        )}
      </ModalItem>
    </>
  );
};

export default ModalMensajeMasivo;
